import { jaJP } from '@clerk/localizations'
import { ClerkProvider } from '@clerk/react-router'
import {
	clerkMiddleware,
	rootAuthLoader,
	getAuth,
	createClerkClient,
} from '@clerk/react-router/server'
import {
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
	data as json,
	redirect,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { type Route } from '../.react-router/types/app/+types/root'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { useToast } from './components/toaster.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { AppToaster } from './components/ui/sonner.tsx'
import { calculateTrialEndAfter } from './modules/contract/trial.ts'
import { AppHeader } from './routes/_components/headers.tsx'
import { getContractInfo } from './routes/api+/tenant.server.ts'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import {
	fetchInvitations,
	getUserId,
	logout,
	syncExternalId,
} from './utils/auth.server.ts'
import { getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// Preload CSS as a resource to avoid render blocking
		// { rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
		// {
		// 	rel: 'alternate icon',
		// 	type: 'image/png',
		// 	href: '/favicons/favicon-32x32.png',
		// },
		// { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
		// {
		// 	rel: 'manifest',
		// 	href: '/site.webmanifest',
		// 	crossOrigin: 'use-credentials',
		// } as const, // necessary to make typescript happy
		//These should match the css preloads above to avoid css as render blocking resource
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? 'FirstHR' : 'Error | FirstHR' },
		{ name: 'description', content: `FirstHR` },
	]
}

export const middleware: Route.MiddlewareFunction[] = [clerkMiddleware()]

export function loader(args: Route.LoaderArgs) {
	return rootAuthLoader(args, async ({ request }) => {
		const timings = makeTimings('root loader')
		const { userId: clerkUserId } = await getAuth(args)
		const userId = await time(() => getUserId(args), {
			timings,
			type: 'getUserId',
			desc: 'getUserId in root',
		})

		const user = userId
			? await time(
					() =>
						prisma.user.findUnique({
							select: {
								id: true,
								firstSignInAt: true,
								roles: {
									select: {
										name: true,
										permissions: {
											select: { entity: true, action: true, access: true },
										},
									},
								},
								employee: {
									select: {
										code: true,
										name: true,
										email: true,
										company: {
											select: {
												id: true,
												name: true,
												clerkOrganizationId: true,
											},
										},
									},
								},
							},
							where: { id: userId },
						}),
					{ timings, type: 'find user', desc: 'find user in root' },
				)
			: null
		if (userId && !user) {
			console.info('something weird happened')
			// something weird happened... The user is authenticated but we can't find
			// them in the database. Maybe they were deleted? Let's log them out.
			const { sessionId } = request.auth
			if (sessionId) {
				await logout(sessionId)
			}
		}
		const path = new URL(request.url).pathname
		let contractInfo
		if (user && user.employee.company) {
			contractInfo = await getContractInfo(user.employee.company.id)
		}
		if (
			user &&
			user.employee.company &&
			contractInfo &&
			path !== '/unavailable'
		) {
			if (!contractInfo.isActive) {
				console.info(
					`契約期間終了後にアクセスがありました。 [ユーザーID:${user.id}][企業ID:${user.employee.company.id}]`,
				)
				throw redirect('/unavailable')
			}
		}
		const clerkClient = createClerkClient({
			secretKey: process.env.CLERK_SECRET_KEY,
		})
		// 認証サービスのOrganizationに所属させる
		if (user && clerkUserId) {
			const organizationId = user.employee.company.clerkOrganizationId
			if (organizationId && organizationId !== '') {
				const { totalCount } =
					await clerkClient.users.getOrganizationMembershipList({
						userId: clerkUserId,
						limit: 1,
					})
				if (totalCount === 0) {
					await clerkClient.organizations.createOrganizationMembership({
						organizationId,
						userId: clerkUserId,
						role: 'org:member',
					})
				}
			}
		}
		//招待済みであるかを保存するため初回のサインイン日時を登録する
		if (user && !user.firstSignInAt) {
			await prisma.user.update({
				where: {
					id: user.id,
				},
				data: {
					firstSignInAt: new Date(),
				},
			})
			//招待済みにも関わらずClerkの招待データが消えない場合があるので、明示的に削除する。
			const pendingInvitations = await fetchInvitations('pending')
			//１つのアドレスに複数のpendingデータが存在する
			const foundPendingInvitations = pendingInvitations.filter(
				invitation => invitation.emailAddress === user.employee.email,
			)
			if (foundPendingInvitations.length > 0) {
				for await (const foundPendingInvitation of foundPendingInvitations) {
					await clerkClient.invitations.revokeInvitation(
						foundPendingInvitation.id,
					)
				}
			}
		}
		// Clerkのユーザーとdbのユーザー情報をIDで紐付け
		if (user && userId) {
			await syncExternalId(args, userId)
		}

		let showTrialLabel = false
		let numberOfTrialDaysRemaining = 0
		if (contractInfo) {
			const today = new Date()
			showTrialLabel = contractInfo.type === 'trial'
			numberOfTrialDaysRemaining = contractInfo.endedAt
				? calculateTrialEndAfter(contractInfo.endedAt, today)
				: 0
		}
		const { toast, headers: toastHeaders } = await getToast(request)
		return json(
			{
				user,
				showTrialLabel,
				numberOfTrialDaysRemaining,
				requestInfo: {
					hints: getHints(request),
					origin: getDomainUrl(request),
					path,
				},
				ENV: getEnv(),
				toast,
			},
			{
				headers: combineHeaders(
					{ 'Server-Timing': timings.toString() },
					toastHeaders,
				),
			},
		)
	})
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	env?: Record<string, string>
}) {
	return (
		<html lang="ja" className={`h-full`}>
			<head>
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<meta name="robots" content="noindex, nofollow" />
				<Links />
			</head>
			<body className="text-foreground bg-[#F7F7F7] font-sans">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export default function App({ loaderData }: Route.ComponentProps) {
	const nonce = useNonce()
	const data = useLoaderData<typeof loader>()
	//@ts-ignore
	useToast(data.toast)
	//@ts-ignore
	if (data.requestInfo.path.startsWith('/popup/')) {
		return (
			<>
				<Document nonce={nonce}>
					<Outlet />
				</Document>
			</>
		)
	}
	return (
		<ClerkProvider loaderData={loaderData} localization={jaJP}>
			<Document nonce={nonce}>
				<div className="pb-[48px]">
					<AppHeader
						//@ts-ignore
						companyName={data.user?.employee.company?.name}
						//@ts-ignore
						employeeName={data.user?.employee?.name}
						//@ts-ignore
						role={data.user?.roles[0]?.name}
						//@ts-ignore
						showTrialLabel={data.showTrialLabel}
						//@ts-ignore
						numberOfTrialDaysRemaining={data.numberOfTrialDaysRemaining}
					/>
				</div>
				<div className="h-screen justify-between">
					<div className="px-[16px] py-4 md:px-[32px] md:py-8 lg:px-[80px] lg:py-12">
						<Outlet />
					</div>
				</div>
				<AppToaster closeButton position="top-center" />
			</Document>
		</ClerkProvider>
	)
}

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<AppHeader />
			<GeneralErrorBoundary
				statusHandlers={{
					403: () => (
						<div className="flex flex-col gap-6">
							<div className="flex flex-col gap-3">
								<h1>お探しのページは見つかりませんでした。</h1>
							</div>
							<Link to="/" className="text-body-md underline">
								<Icon name="arrow-left">トップページへ戻る</Icon>
							</Link>
						</div>
					),
				}}
			/>
		</Document>
	)
}
